import { Dropdown, Empty, Modal, message } from 'antd';
import type { DropdownProps } from 'antd';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from '@umijs/max';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import {
  MEMBER_ASSET_SOURCE_AI,
  MEMBER_ASSET_SOURCE_UPLOAD,
  MEMBER_ASSET_TYPE_IMAGE,
  MEMBER_ASSET_TYPE_VIDEO,
  deleteMemberAsset,
  fetchMemberAssetPage,
  uploadMemberAsset,
  type MemberAssetPageItem,
} from '@/services/member/asset';
import { getDefaultWorkspaceId } from '@/utils/memberWorkspaceCache';
import './style.less';

export type AssetKind = 'image' | 'video';

export type AssetType = 2 | 3; // 2-IMAGE(图片), 3-VIDEO(视频)
export type AssetItem = { id: string; assetType: AssetType; url: string };

const VIDEO_URL_PATTERN = /\.(mp4|webm|mov|m4v|avi|mkv)(\?|$)/i;

function inferAssetTypeFromUrl(url: string): AssetType {
  const path = url.split('?')[0] ?? url;
  return VIDEO_URL_PATTERN.test(path) ? 3 : 2;
}

function mapPageItemToAssetItem(item: MemberAssetPageItem): AssetItem | null {
  const url = item.assetData?.trim();
  if (!url) return null;
  const assetType: AssetType =
    item.assetType === MEMBER_ASSET_TYPE_VIDEO
      ? 3
      : item.assetType === MEMBER_ASSET_TYPE_IMAGE
        ? 2
        : inferAssetTypeFromUrl(url);
  return {
    id: String(item.id),
    assetType,
    url,
  };
}

export type AssetSelectorProps = {
  kind: AssetKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  popupContainer?: HTMLElement | null;
  width?: number | 'trigger' | 'container';
  placement?: DropdownProps['placement'];
  triggerStyle?: React.CSSProperties;
  selectedIds?: string[];
  onToggleSelect?: (asset: AssetItem, selected: boolean) => void;
  /** 在这些元素上发生的点击，不触发 Dropdown outside-click 关闭 */
  ignoreOutsideCloseSelector?: string;
  children: React.ReactNode;
};

export const AssetSelector: React.FC<AssetSelectorProps> = ({
  open,
  onOpenChange,
  kind,
  popupContainer,
  width = 'container',
  placement = 'bottomLeft',
  triggerStyle,
  selectedIds,
  onToggleSelect,
  ignoreOutsideCloseSelector = '[data-asset-preview-delete="1"]',
  children,
}) => {
  const intl = useIntl();
  const [tab, setTab] = useState<'upload' | 'ai'>('upload');
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const openRef = useRef(open);
  const ignoreNextCloseRef = useRef(false);
  const ignoreResetTimerRef = useRef<number | null>(null);
  const [panelWidth, setPanelWidth] = useState<number | undefined>(undefined);
  const [assets, setAssets] = useState<AssetItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const typeText = intl.formatMessage({
    id: kind === 'video' ? 'assets.selector.type.video' : 'assets.selector.type.image',
  });

  const title = intl.formatMessage(
    { id: 'assets.selector.title' },
    {
      type: typeText,
    },
  );

  const titleIconClass = useMemo(() => {
    if (kind === 'video') return 'iconfont icon-video';
    return 'iconfont icon-Images';
  }, [kind]);

  const computeWidth = () => {
    if (typeof width === 'number') return width;
    if (width === 'trigger') {
      const w = triggerRef.current?.getBoundingClientRect().width;
      return typeof w === 'number' && w > 0 ? Math.round(w) : undefined;
    }
    const w = popupContainer?.getBoundingClientRect().width;
    return typeof w === 'number' && w > 0 ? Math.round(w) : undefined;
  };

  useLayoutEffect(() => {
    if (!open) return;
    setPanelWidth(computeWidth());
  }, [open, width, popupContainer]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    if (typeof width === 'number') return;

    const target: Element | null =
      width === 'trigger' ? triggerRef.current : popupContainer ?? null;
    if (!target || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => {
      setPanelWidth(computeWidth());
    });
    ro.observe(target);
    return () => ro.disconnect();
  }, [open, width, popupContainer]);

  const loadAssets = async (activeTab: 'upload' | 'ai', workspaceId?: number) => {
    const assetSource =
      activeTab === 'upload' ? MEMBER_ASSET_SOURCE_UPLOAD : MEMBER_ASSET_SOURCE_AI;
    const records = await fetchMemberAssetPage({
      assetSource,
      ...(workspaceId != null ? { workspaceId } : {}),
    });
    return records
      .map(mapPageItemToAssetItem)
      .filter((item): item is AssetItem => item != null);
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await loadAssets(tab, getDefaultWorkspaceId());
        if (cancelled) return;
        setAssets(data);
      } catch (error: unknown) {
        if (cancelled) return;
        const errMsg =
          error instanceof Error
            ? error.message
            : intl.formatMessage({ id: 'assets.selector.loadFailed' });
        message.error(errMsg);
        setAssets([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tab]);

  // 统一管理 outside-click 的“忽略关闭”逻辑：
  // 只在捕获阶段打标记，不拦截事件传播，避免影响按钮点击本身。
  useLayoutEffect(() => {
    const handler = (e: Event) => {
      if (!openRef.current) return;
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (ignoreOutsideCloseSelector && t.closest(ignoreOutsideCloseSelector)) {
        ignoreNextCloseRef.current = true;
        // 只忽略“本次删除点击”触发的关闭。事件循环结束后自动清除，
        // 避免出现“需要点两次空白处才能关闭”的问题。
        if (ignoreResetTimerRef.current) window.clearTimeout(ignoreResetTimerRef.current);
        ignoreResetTimerRef.current = window.setTimeout(() => {
          ignoreNextCloseRef.current = false;
          ignoreResetTimerRef.current = null;
        }, 0);
      }
    };

    document.addEventListener('pointerdown', handler, true);
    document.addEventListener('mousedown', handler, true);
    document.addEventListener('click', handler, true);
    return () => {
      document.removeEventListener('pointerdown', handler, true);
      document.removeEventListener('mousedown', handler, true);
      document.removeEventListener('click', handler, true);
      if (ignoreResetTimerRef.current) window.clearTimeout(ignoreResetTimerRef.current);
      ignoreResetTimerRef.current = null;
    };
  }, [ignoreOutsideCloseSelector]);

  const visibleAssets = useMemo(() => {
    const wantType: AssetType = kind === 'video' ? 3 : 2;
    return assets.filter((a) => a.assetType === wantType).slice(0, 8);
  }, [assets, kind]);

  const selectedIdSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);

  const toggleSelect = (a: AssetItem) => {
    const nextSelected = !selectedIdSet.has(a.id);
    onToggleSelect?.(a, nextSelected);
  };

  const acceptMime = kind === 'video' ? 'video/*' : 'image/*';

  const handleUploadClick = () => {
    if (uploading) return;
    // workspaceId：/app/member/info 返回 workspaceIdList 的第一项（已写入浏览器缓存）
    if (getDefaultWorkspaceId() == null) {
      message.warning(intl.formatMessage({ id: 'assets.selector.uploadNoWorkspace' }));
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const workspaceId = getDefaultWorkspaceId();
    if (workspaceId == null) {
      message.warning(intl.formatMessage({ id: 'assets.selector.uploadNoWorkspace' }));
      return;
    }

    setUploading(true);
    try {
      await uploadMemberAsset(file, { workspaceId });
      setTab('upload');
      const data = await loadAssets('upload', workspaceId);
      setAssets(data);
      message.success(intl.formatMessage({ id: 'assets.selector.uploadSuccess' }));
    } catch (error: unknown) {
      const errMsg =
        error instanceof Error
          ? error.message
          : intl.formatMessage({ id: 'assets.selector.uploadFailed' });
      message.error(errMsg);
    } finally {
      setUploading(false);
    }
  };

  const requestRemove = (a: AssetItem) => {
    const memberAssetId = Number(a.id);
    if (!Number.isInteger(memberAssetId) || memberAssetId <= 0) {
      message.error(intl.formatMessage({ id: 'assets.selector.deleteFailed' }));
      return;
    }

    setDeleteConfirmOpen(true);
    Modal.confirm({
      title: intl.formatMessage({ id: 'assets.selector.deleteConfirm.title' }),
      content: intl.formatMessage({ id: 'assets.selector.deleteConfirm.content' }),
      okText: intl.formatMessage({ id: 'assets.selector.deleteConfirm.ok' }),
      cancelText: intl.formatMessage({ id: 'assets.selector.deleteConfirm.cancel' }),
      okType: 'danger',
      centered: true,
      onOk: async () => {
        try {
          await deleteMemberAsset(memberAssetId);
          setAssets((prev) => prev.filter((x) => x.id !== a.id));
          onToggleSelect?.(a, false);
          message.success(intl.formatMessage({ id: 'assets.selector.deleteSuccess' }));
          setDeleteConfirmOpen(false);
        } catch (error: unknown) {
          const errMsg =
            error instanceof Error
              ? error.message
              : intl.formatMessage({ id: 'assets.selector.deleteFailed' });
          message.error(errMsg);
          setDeleteConfirmOpen(false);
          return Promise.reject(error);
        }
      },
      onCancel: () => {
        setDeleteConfirmOpen(false);
      },
    });
  };

  const panel = (
    <div
      className="assetSelectorDropdown"
      style={{
        width: panelWidth ? `${panelWidth}px` : undefined,
      }}
    >
      <div className="assetSelectorHeader">
        <div className="assetSelectorTitle">
          <span className="assetSelectorTitleIcon" aria-hidden>
            <i className={titleIconClass} aria-hidden />
          </span>
          <span className="assetSelectorTitleText">{title}</span>
        </div>
      </div>

      <div className="assetSelectorBody">
        <div className="assetSelectorUpload">
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptMime}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <button
            type="button"
            className="assetSelectorUploadBox"
            disabled={uploading}
            onClick={handleUploadClick}
          >
            <span className="assetSelectorUploadIcon" aria-hidden>
              <UploadOutlined />
            </span>
            <span className="assetSelectorUploadText">
              {uploading
                ? intl.formatMessage({ id: 'assets.selector.uploading' })
                : intl.formatMessage({ id: 'assets.selector.localUpload' })}
            </span>
          </button>
        </div>

        <div className="assetSelectorMain">
          <div className="assetSelectorTabBar">
            <div className="assetSelectorTabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'upload'}
              className={tab === 'upload' ? 'assetSelectorTab active' : 'assetSelectorTab'}
              onClick={() => setTab('upload')}
            >
              <i className="iconfont icon-file_folder_upload" aria-hidden />
              {intl.formatMessage({ id: 'assets.selector.tab.upload' })}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'ai'}
              className={tab === 'ai' ? 'assetSelectorTab active' : 'assetSelectorTab'}
              onClick={() => setTab('ai')}
            >
              <i className="iconfont icon-sparkles" aria-hidden />
              {intl.formatMessage({ id: 'assets.selector.tab.ai' })}
            </button>
            </div>
          </div>

          <div className="assetSelectorContent">
            {loading ? (
              <div className="assetSelectorEmptyWrap">
                <div className="assetSelectorLoading">
                  {intl.formatMessage({ id: 'assets.selector.loading' })}
                </div>
              </div>
            ) : visibleAssets.length === 0 ? (
              <div className="assetSelectorEmptyWrap">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            ) : (
              <div className="assetSelectorGrid">
                {visibleAssets.map((a) => (
                  <article
                    key={a.id}
                    role="button"
                    tabIndex={0}
                    className={
                      selectedIdSet.has(a.id)
                        ? 'assetSelectorCard assetSelectorCardSelected'
                        : 'assetSelectorCard'
                    }
                    onClick={() => toggleSelect(a)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleSelect(a);
                      }
                    }}
                  >
                    <div className="assetSelectorThumbWrap">
                      {a.assetType === 2 ? (
                        <img
                          className="assetSelectorThumbMedia"
                          src={a.url}
                          alt=""
                          draggable={false}
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <video
                          className="assetSelectorThumbMedia"
                          src={a.url}
                          muted
                          playsInline
                          preload="metadata"
                          referrerPolicy="no-referrer"
                        />
                      )}
                      <button
                        type="button"
                        className="assetSelectorDelete"
                        aria-label={intl.formatMessage({ id: 'assets.selector.deleteConfirm.ok' })}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          requestRemove(a);
                        }}
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={(nextOpen) => {
        // 删除确认框打开时，避免点击“取消/删除”导致 Dropdown 误判为外部点击而自动关闭
        if (!nextOpen && deleteConfirmOpen) return;
        // 预览区删除按钮触发的“外部点击关闭”应被忽略
        if (!nextOpen && ignoreNextCloseRef.current) {
          ignoreNextCloseRef.current = false;
          return;
        }
        onOpenChange(nextOpen);
      }}
      placement={placement}
      overlayClassName="assetSelectorDropdownOverlay"
      popupRender={() => panel}
      getPopupContainer={
        popupContainer ? (() => popupContainer) : (undefined as DropdownProps['getPopupContainer'])
      }
    >
      <span ref={triggerRef} style={{ display: 'inline-flex', ...triggerStyle }}>
        {children}
      </span>
    </Dropdown>
  );
};

